// src/phone-number-regex.ts
var phoneSchemas = {
  de: {
    countryPrefixRegex: /^\+49/,
    mobileRegex: /^\+49(1[5-7]\d{8,9})$/,
    landlineRegex: /^\+49[1-9]\d{8,14}$/
  }
};
var defaultCountry = {
  countryPrefixRegex: /^\+/,
  mobileRegex: /^\+[1-9]\d{7,15}$/,
  landlineRegex: /^\+[1-9]\d{7,15}$/
};
var findSchemaForPhoneNumber = (phoneNumber) => {
  const schema = Object.values(phoneSchemas).find((schema2) => {
    return schema2.countryPrefixRegex.test(phoneNumber);
  });
  return schema || defaultCountry;
};

// src/phone-validators.ts
var isValidPhoneNumber = (phoneNumber) => {
  const phoneSchema = findSchemaForPhoneNumber(phoneNumber);
  const { mobileRegex, landlineRegex } = phoneSchema;
  return mobileRegex.test(phoneNumber) || landlineRegex.test(phoneNumber);
};
var isValidMobilePhoneNumber = (phoneNumber) => {
  return findSchemaForPhoneNumber(phoneNumber).mobileRegex.test(phoneNumber);
};
var isLandlinePhoneNumber = (phoneNumber) => {
  return findSchemaForPhoneNumber(phoneNumber).landlineRegex.test(phoneNumber);
};
function validateMobilePhoneNumber(phoneNumber) {
  const phoneSchema = findSchemaForPhoneNumber(phoneNumber);
  const { mobileRegex, landlineRegex } = phoneSchema;
  if (!mobileRegex.test(phoneNumber)) {
    if (landlineRegex.test(phoneNumber)) {
      return {
        success: false,
        phoneNumber,
        error: `Its a landlinePhone number, got: ${phoneNumber}`,
        errorType: "landlinePhone"
      };
    }
    return {
      success: false,
      phoneNumber,
      error: `Invalid phone number format, got: ${phoneNumber}`,
      errorType: "unknown"
    };
  }
  return { success: true, phoneNumber };
}

// src/normalize-phone-number.ts
function removeInvalidCharacters(phoneNumber) {
  return phoneNumber.replaceAll(/[^\d+()]/g, "");
}
function removeAllInvalidCharacters(phoneNumber) {
  return phoneNumber.replaceAll(/[^\d+]/g, "");
}
function removeZeroInParenthesesPattern(phoneNumber) {
  return phoneNumber.replaceAll(/\(0\)/g, "");
}
function replaceDoubleZeroWithPlus(phoneNumber) {
  if (phoneNumber.startsWith("00")) {
    return `+${phoneNumber.slice(2)}`;
  }
  return phoneNumber;
}
function replaceDoublePlusWithPlus(phoneNumber) {
  if (phoneNumber.startsWith("++")) {
    return `+${phoneNumber.slice(2)}`;
  }
  return phoneNumber;
}
function replaceDoublePlusZeroWithPlus(phoneNumber) {
  if (phoneNumber.startsWith("+00")) {
    return `+${phoneNumber.slice(3)}`;
  }
  if (phoneNumber.startsWith("+0")) {
    return `+49${phoneNumber.slice(2)}`;
  }
  return phoneNumber;
}
function stripLeading(phoneNumber, character) {
  let index = 0;
  while (phoneNumber.charAt(index) === character) {
    index += 1;
  }
  return index === 0 ? phoneNumber : phoneNumber.slice(index);
}
function removeLeadingZeros(phoneNumber) {
  return stripLeading(phoneNumber, "0");
}
function removeLeadingPluses(phoneNumber) {
  return stripLeading(phoneNumber, "+");
}
function fixDuplicateCountryCode(phoneNumber) {
  const duplicateCountryCodeRegex = /^\+(\d{1,2})([^\d+]*)\+\1(.*)$/;
  const duplicateMatch = phoneNumber.match(duplicateCountryCodeRegex);
  if (duplicateMatch) {
    return [`+${duplicateMatch[1]}`, duplicateMatch[3] || ""];
  }
  const singleCountryCodeRegex = /^\+(\d{1,2})(.*)$/;
  const singleMatch = phoneNumber.match(singleCountryCodeRegex);
  if (singleMatch) {
    return [`+${singleMatch[1]}`, singleMatch[2] || ""];
  }
  if (phoneNumber.startsWith("0")) {
    return ["+49", phoneNumber.substring(1)];
  }
  return ["+49", phoneNumber];
}
var KNOWN_EMBEDDED_COUNTRY_CODES = /^(?:49|43)/;
function preferEmbeddedInternationalNumber(prefix, rest) {
  if (!rest.startsWith("00")) {
    return [prefix, rest];
  }
  const embeddedNumber = rest.slice(2);
  if (!KNOWN_EMBEDDED_COUNTRY_CODES.test(embeddedNumber)) {
    return [prefix, rest];
  }
  return fixDuplicateCountryCode(`+${embeddedNumber}`);
}
var normalizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return {
      success: false,
      error: "Invalid input",
      errorType: "invalidInput"
    };
  }
  let cleaned = phoneNumber.trim();
  cleaned = removeInvalidCharacters(cleaned);
  cleaned = replaceDoubleZeroWithPlus(cleaned);
  cleaned = replaceDoublePlusWithPlus(cleaned);
  cleaned = replaceDoublePlusZeroWithPlus(cleaned);
  cleaned = removeZeroInParenthesesPattern(cleaned);
  cleaned = removeAllInvalidCharacters(cleaned);
  let [prefix, rest] = fixDuplicateCountryCode(cleaned);
  [prefix, rest] = preferEmbeddedInternationalNumber(prefix, rest);
  rest = removeLeadingZeros(rest);
  rest = removeLeadingPluses(rest);
  cleaned = prefix + rest;
  return validateMobilePhoneNumber(cleaned);
};
export {
  isLandlinePhoneNumber,
  isValidMobilePhoneNumber,
  isValidPhoneNumber,
  normalizePhoneNumber,
  validateMobilePhoneNumber
};
//# sourceMappingURL=index.js.map